import React from 'react';
import { Link } from 'react-router-dom';
import { User, Users, UsersRound, Laptop, Apple, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Services = () => {
  const services = [
    {
      icon: User,
      title: 'Coaching Privé',
      description: 'Séance individuelle 100% personnalisée pour des résultats optimaux',
      features: [
        'Attention exclusive du coach',
        'Programme totalement adapté à vos besoins',
        'Progression rapide et mesurable',
        'Flexibilité des horaires',
        'Suivi personnalisé continu'
      ],
      image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/yzz428ta_IMG_7115.png',
      color: 'red'
    },
    {
      icon: Users,
      title: 'Coaching Duo',
      description: 'Entraînez-vous à deux pour une motivation décuplée',
      features: [
        'Partagez l\'expérience avec un proche',
        'Motivation mutuelle constante',
        'Programme adapté à vos deux profils',
        'Tarif avantageux',
        'Ambiance conviviale et dynamique'
      ],
      image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/r3momt1h_IMG_7116.png',
      color: 'red'
    },
    {
      icon: UsersRound,
      title: 'Coaching Groupe',
      description: 'Sessions dynamiques en petit groupe pour plus d\'énergie collective',
      features: [
        'Groupes de 4 à 8 personnes maximum',
        'Émulation et esprit d\'équipe',
        'Exercices variés et stimulants',
        'Ambiance motivante',
        'Prix accessible'
      ],
      image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/dicl0r9b_IMG_7133.jpeg',
      color: 'red'
    },
    {
      icon: Laptop,
      title: 'Coaching En Ligne',
      description: 'Programme personnalisé à suivre où vous voulez, quand vous voulez',
      features: [
        'Programme d\'entraînement personnalisé',
        'Vidéos explicatives des exercices',
        'Suivi hebdomadaire à distance',
        'Flexibilité totale',
        'Ajustements réguliers du programme'
      ],
      image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/3c2x2xi9_IMG_7134.jpeg',
      color: 'red'
    },
    {
      icon: Apple,
      title: 'Programme Nutrition',
      description: 'Plan nutritionnel adapté à vos besoins et objectifs spécifiques',
      features: [
        'Analyse complète de vos habitudes',
        'Plan alimentaire personnalisé',
        'Recettes simples et savoureuses',
        'Suivi et ajustements réguliers',
        'Compatible avec vos contraintes'
      ],
      image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/8txku760_IMG_7135.jpeg',
      color: 'red'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-black"></div>
        <div className="relative container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Nos <span className="text-red-600">Services</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Un accompagnement personnalisé pour tous les profils : débutants, perte de poids, 
            prise de masse, amélioration des performances. Pour tous les âges.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="relative group overflow-hidden rounded-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[500px] object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-6 left-6">
                      <service.icon className="w-12 h-12 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="flex items-center mb-4">
                    <service.icon className="w-10 h-10 text-red-600 mr-4" />
                    <h2 className="text-4xl font-bold text-white">{service.title}</h2>
                  </div>
                  <p className="text-xl text-gray-400 mb-8">{service.description}</p>
                  
                  <div className="space-y-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-gray-300 text-lg">{feature}</p>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white group">
                      Réserver Ce Service
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-20 bg-black border-t border-red-600">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            Adapté à Tous Vos <span className="text-red-600">Objectifs</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Perte de Poids', description: 'Programme complet pour perdre du poids durablement' },
              { title: 'Prise de Masse', description: 'Développez votre masse musculaire efficacement' },
              { title: 'Transformation Physique', description: 'Sculptez le corps dont vous rêvez' },
              { title: 'Remise en Forme', description: 'Retrouvez votre forme et votre énergie' },
              { title: 'Performance Sportive', description: 'Améliorez vos performances athlétiques' },
              { title: 'Bien-être Général', description: 'Maintenez une santé optimale au quotidien' }
            ].map((objective, index) => (
              <div
                key={index}
                className="bg-zinc-900 p-8 rounded-lg hover:bg-zinc-800 transition-all hover:scale-105"
              >
                <h3 className="text-xl font-bold text-white mb-3">{objective.title}</h3>
                <p className="text-gray-400">{objective.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Quel Service Vous Convient Le Mieux ?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contactez Coach Massa pour discuter de vos objectifs et trouver la formule idéale
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-black hover:bg-zinc-900 text-white px-8 py-6 text-lg">
              Discuter de Mes Objectifs
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
